// The page's AI, its pure parts: which session is a page's own thread, the
// starters its composer offers, the desktop panel's remembered state and width
// rules, the canvas on the thread, the sandboxed document for an SVG or a
// mock-up, and the whiteboard's strokes. No React here, so all of it is tested.
import type { AgentMessage, AgentSessionResource } from '@/kinds/agentsession'

export type Canvas = NonNullable<AgentMessage['canvas']>
export type PanelTab = 'thread' | 'canvas' | 'board'

export const PANEL_MIN = 320
export const PANEL_MAX = 720
export const PANEL_DEFAULT = 400
/** The panel docks only beside a page on a screen this wide; below it the thread keeps its own screen. */
export const PANEL_QUERY = '(min-width: 1100px)'

export type PanelState = { open: boolean; width: number; tab: PanelTab }

/**
 * A page's own thread: the newest live session bound to it. One per page, made
 * by the first message sent from the panel or the Agent tab; both show this one.
 */
export const pageThreadOf = (
  sessions: AgentSessionResource[],
  pageId: string,
): AgentSessionResource | undefined =>
  sessions
    .filter((s) => s.spec.page_id === pageId && s.status.state === 'active' && !s.deleted_at)
    .sort((a, b) => ((a.status.last_at ?? a.created_at) < (b.status.last_at ?? b.created_at) ? 1 : -1))[0]

/**
 * One-tap starters on a page's thread. Each is an ordinary message the brain
 * answers with its usual tools (a page_write card for writing, Task creates
 * for tasks); `send: false` only puts the words in the box to finish.
 */
export type Starter = { label: string; text: string; send: boolean }
export const PAGE_STARTERS: Starter[] = [
  { label: 'Write…', text: 'Write ', send: false },
  { label: 'Continue writing', text: 'Continue writing this page.', send: true },
  { label: 'Summarise', text: 'Summarise this page.', send: true },
  { label: 'Tidy this up', text: 'Tidy this up.', send: true },
  { label: 'Pull out tasks', text: 'Pull out the tasks this page implies.', send: true },
]

const TABS: PanelTab[] = ['thread', 'canvas', 'board']

/** The widest the panel may be: its maximum, and never more than 60% of the window. */
export const clampWidth = (w: number, viewport: number): number => {
  const max = Math.max(PANEL_MIN, Math.min(PANEL_MAX, Math.round(viewport * 0.6)))
  if (!Number.isFinite(w)) return PANEL_DEFAULT
  return Math.min(max, Math.max(PANEL_MIN, Math.round(w)))
}

/** What was remembered on this device, repaired: anything unreadable is the default (open, 400, thread). */
export const parsePanel = (raw: string | null): PanelState => {
  const out: PanelState = { open: true, width: PANEL_DEFAULT, tab: 'thread' }
  if (!raw) return out
  try {
    const v = JSON.parse(raw) as Partial<PanelState> | null
    if (!v || typeof v !== 'object') return out
    if (typeof v.open === 'boolean') out.open = v.open
    if (typeof v.width === 'number' && Number.isFinite(v.width))
      out.width = Math.min(PANEL_MAX, Math.max(PANEL_MIN, Math.round(v.width)))
    if (v.tab && TABS.includes(v.tab)) out.tab = v.tab
  } catch {
    /* unreadable: the default */
  }
  return out
}

/** Every canvas on the thread, oldest first; the newest is what the panel shows. */
export const canvasesOf = (messages: AgentMessage[]): Canvas[] =>
  messages.flatMap((m) => (m.role === 'assistant' && m.canvas ? [m.canvas] : []))

/**
 * The document an SVG or a mock-up is shown in. The frame is sandboxed with no
 * permissions (no scripts, no same origin, no forms, no popups) and the
 * document says the same to the browser a second time: nothing is fetched but
 * data: images, no script runs, nothing submits, no base can be set.
 */
export const FRAME_CSP =
  "default-src 'none'; img-src data:; font-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"

export const frameDoc = (kind: Canvas['kind'], content: string): string => {
  const body =
    kind === 'svg'
      ? `<div style="display:flex;align-items:center;justify-content:center;min-height:100vh;padding:16px;box-sizing:border-box">${content}</div>`
      : content
  return (
    `<!doctype html><html><head><meta charset="utf-8">` +
    `<meta http-equiv="Content-Security-Policy" content="${FRAME_CSP}">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1">` +
    `<style>html{color-scheme:light dark}body{margin:0;font-family:system-ui,sans-serif}svg{max-width:100%;height:auto}</style>` +
    `</head><body>${body}</body></html>`
  )
}

/** The attributes the frame must carry: an empty sandbox, nothing allowed, nothing sent. */
export const FRAME_SANDBOX = ''

// ---- the whiteboard ----------------------------------------------------------

export type Point = [number, number]
export type Stroke = Point[]

const MAX_STROKES = 400
const MAX_POINTS = 2000

/** Drop points closer than `gap` to the last kept one, always keeping the end. */
export const thin = (pts: Stroke, gap = 1.5): Stroke => {
  const first = pts[0]
  const last = pts[pts.length - 1]
  if (pts.length < 3 || !first || !last) return pts
  const out: Stroke = [first]
  let [x, y] = first
  for (const p of pts.slice(1, -1)) {
    if (Math.hypot(p[0] - x, p[1] - y) >= gap) {
      out.push(p)
      ;[x, y] = p
    }
  }
  out.push(last)
  return out
}

/** An SVG path for a stroke; a single point is a dot. */
export const strokePath = (pts: Stroke): string => {
  const first = pts[0]
  if (!first) return ''
  const f = (n: number) => Math.round(n * 10) / 10
  if (pts.length === 1) return `M${f(first[0])} ${f(first[1])}h0.01`
  return pts.map(([x, y], i) => `${i ? 'L' : 'M'}${f(x)} ${f(y)}`).join('')
}

/** Stored strokes, repaired: anything that is not a list of points is dropped, and the size is capped. */
export const parseStrokes = (raw: string | null): Stroke[] => {
  if (!raw) return []
  try {
    const v = JSON.parse(raw) as unknown
    if (!Array.isArray(v)) return []
    const out: Stroke[] = []
    for (const s of v.slice(-MAX_STROKES)) {
      if (!Array.isArray(s)) continue
      const pts: Stroke = []
      for (const p of s.slice(0, MAX_POINTS)) {
        if (
          Array.isArray(p) &&
          typeof p[0] === 'number' &&
          typeof p[1] === 'number' &&
          isFinite(p[0]) &&
          isFinite(p[1])
        )
          pts.push([p[0], p[1]])
      }
      if (pts.length) out.push(pts)
    }
    return out
  } catch {
    return []
  }
}

export const boardKey = (pageId: string) => `whiteboard:${pageId}`
